import { useEffect, useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { Action, Location } from 'history';

// Confirms before the user leaves a dirty form via react-router's own history.block(), which
// gates every in-app navigation centrally -- Cancel, sidebar links, header/profile links -- with
// no per-call-site wrapping. Denying (`false`) and replaying the location on confirm keeps the
// dialog our own ConfirmModal. Does not cover a full page redirect (Log Out) or a closed tab.
export function useUnsavedChangesGuard(hasUnsavedChanges: boolean) {
  const history = useHistory();
  const hasUnsavedChangesRef = useRef(hasUnsavedChanges);
  const bypassNextRef = useRef(false);
  const [pendingLocation, setPendingLocation] = useState<Location | null>(null);
  const [pendingAction, setPendingAction] = useState<Action | null>(null);

  useEffect(() => {
    hasUnsavedChangesRef.current = hasUnsavedChanges;
  }, [hasUnsavedChanges]);

  useEffect(() => history.block((location: Location, action: Action) => {
    if (bypassNextRef.current) {
      bypassNextRef.current = false;
      return undefined;
    }
    if (!hasUnsavedChangesRef.current) {
      return undefined;
    }
    setPendingLocation(location);
    setPendingAction(action);
    return false;
  }), [history]);

  const confirmLeave = () => {
    if (!pendingLocation) {
      return;
    }
    bypassNextRef.current = true;
    // A blocked POP (browser back/forward) is already reverted by history itself -- replaying it
    // with push() would append a NEW entry instead of resuming the original browser navigation,
    // corrupting the stack so a later Back click bounces the user right back onto this page.
    if (pendingAction === 'POP') {
      history.goBack();
    } else {
      history.push(pendingLocation);
    }
    setPendingLocation(null);
    setPendingAction(null);
  };

  const cancelLeave = () => {
    setPendingLocation(null);
    setPendingAction(null);
  };

  const allowNextNavigation = () => {
    bypassNextRef.current = true;
  };

  return {
    isConfirmVisible: pendingLocation !== null,
    confirmLeave,
    cancelLeave,
    allowNextNavigation,
  };
}
